import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Button,
} from '@mui/material';
import {
  Search as SearchIcon,
  Description as DescriptionIcon,
  MedicalServices as MedicalServicesIcon,
  Schedule as ScheduleIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`orders-tabpanel-${index}`}
      aria-labelledby={`orders-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const Orders: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [diagnosisSearch, setDiagnosisSearch] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);

  // Функция для генерации PDF памятки
  const generatePDFMemo = () => {
    const content = `
<!DOCTYPE html>
<html lang="ru">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Памятка по приказу 211-н</title>
    <style>
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            line-height: 1.6;
            margin: 0;
            padding: 20px;
            background: #f8f9fa;
            color: #333;
        }
        .container {
            max-width: 800px;
            margin: 0 auto;
            background: white;
            padding: 30px;
            border-radius: 12px;
            box-shadow: 0 4px 20px rgba(0,0,0,0.1);
        }
        .header {
            text-align: center;
            margin-bottom: 30px;
            padding-bottom: 20px;
            border-bottom: 2px solid #4A90E2;
        }
        .title {
            font-size: 24px;
            font-weight: 700;
            color: #2D3748;
            margin-bottom: 10px;
        }
        .subtitle {
            font-size: 16px;
            color: #718096;
        }
        .info-box {
            background: #E8F4FD;
            border: 1px solid #4A90E2;
            border-radius: 8px;
            padding: 15px;
            margin-bottom: 25px;
        }
        .info-title {
            font-weight: 600;
            color: #4A90E2;
            margin-bottom: 8px;
        }
        .table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 20px;
        }
        .table th {
            background: #F8FAFC;
            color: #2D3748;
            font-weight: 600;
            padding: 12px;
            text-align: left;
            border: 1px solid #E2E8F0;
        }
        .table td {
            padding: 12px;
            border: 1px solid #E2E8F0;
            vertical-align: top;
        }
        .table tr:nth-child(even) {
            background: #F8FAFC;
        }
        .age {
            font-weight: 600;
            color: #4A90E2;
        }
        .specialists {
            font-size: 14px;
            line-height: 1.4;
        }
        .examinations {
            font-size: 14px;
            line-height: 1.4;
            color: #666;
        }
        .footer {
            margin-top: 30px;
            padding-top: 20px;
            border-top: 1px solid #E2E8F0;
            text-align: center;
            font-size: 12px;
            color: #718096;
        }
        @media print {
            body { background: white; }
            .container { box-shadow: none; }
        }
        @media (max-width: 768px) {
            body { padding: 10px; }
            .container { padding: 20px; }
            .table { font-size: 12px; }
            .table th, .table td { padding: 8px; }
        }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <div class="title">Памятка по приказу 211-н</div>
            <div class="subtitle">Профилактические осмотры несовершеннолетних</div>
        </div>
        
        <div class="info-box">
            <div class="info-title">Источник:</div>
            Приказ Минздрава России от 14 апреля 2025 г. № 211-н "О порядке прохождения несовершеннолетними профилактических медицинских осмотров". Вступает в силу с 01.09.2025.
        </div>

        <table class="table">
            <thead>
                <tr>
                    <th>Возраст</th>
                    <th>Специалисты</th>
                    <th>Обследования</th>
                </tr>
            </thead>
            <tbody>
                ${preventiveExaminations.map(exam => `
                    <tr>
                        <td class="age">${exam.age}</td>
                        <td class="specialists">${exam.specialists.join(' • ')}</td>
                        <td class="examinations">${exam.examinations.length > 0 ? exam.examinations.join(' • ') : 'Не проводится'}</td>
                    </tr>
                `).join('')}
            </tbody>
        </table>

        <div class="footer">
            <p>Сгенерировано: ${new Date().toLocaleDateString('ru-RU')}</p>
            <p>Педиатрическое приложение для врачей</p>
        </div>
    </div>
</body>
</html>`;

    // Создаем blob и скачиваем
    const blob = new Blob([content], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Памятка_приказ_211-н_${new Date().toISOString().split('T')[0]}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Функция для печати памятки
  const printMemo = () => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html lang="ru">
        <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>Памятка по приказу 211-н</title>
            <style>
                body {
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                    line-height: 1.6;
                    margin: 0;
                    padding: 20px;
                    color: #333;
                }
                .header {
                    text-align: center;
                    margin-bottom: 30px;
                    padding-bottom: 20px;
                    border-bottom: 2px solid #4A90E2;
                }
                .title {
                    font-size: 24px;
                    font-weight: 700;
                    color: #2D3748;
                    margin-bottom: 10px;
                }
                .subtitle {
                    font-size: 16px;
                    color: #718096;
                }
                .info-box {
                    background: #E8F4FD;
                    border: 1px solid #4A90E2;
                    border-radius: 8px;
                    padding: 15px;
                    margin-bottom: 25px;
                }
                .info-title {
                    font-weight: 600;
                    color: #4A90E2;
                    margin-bottom: 8px;
                }
                .table {
                    width: 100%;
                    border-collapse: collapse;
                    margin-bottom: 20px;
                }
                .table th {
                    background: #F8FAFC;
                    color: #2D3748;
                    font-weight: 600;
                    padding: 12px;
                    text-align: left;
                    border: 1px solid #E2E8F0;
                }
                .table td {
                    padding: 12px;
                    border: 1px solid #E2E8F0;
                    vertical-align: top;
                }
                .table tr:nth-child(even) {
                    background: #F8FAFC;
                }
                .age {
                    font-weight: 600;
                    color: #4A90E2;
                }
                .specialists {
                    font-size: 14px;
                    line-height: 1.4;
                }
                .examinations {
                    font-size: 14px;
                    line-height: 1.4;
                    color: #666;
                }
                .footer {
                    margin-top: 30px;
                    padding-top: 20px;
                    border-top: 1px solid #E2E8F0;
                    text-align: center;
                    font-size: 12px;
                    color: #718096;
                }
                @media print {
                    body { margin: 0; padding: 15px; }
                    .table { font-size: 12px; }
                    .table th, .table td { padding: 8px; }
                }
            </style>
        </head>
        <body>
            <div class="header">
                <div class="title">Памятка по приказу 211-н</div>
                <div class="subtitle">Профилактические осмотры несовершеннолетних</div>
            </div>
            
            <div class="info-box">
                <div class="info-title">Источник:</div>
                Приказ Минздрава России от 14 апреля 2025 г. № 211-н "О порядке прохождения несовершеннолетними профилактических медицинских осмотров". Вступает в силу с 01.09.2025.
            </div>

            <table class="table">
                <thead>
                    <tr>
                        <th>Возраст</th>
                        <th>Специалисты</th>
                        <th>Обследования</th>
                    </tr>
                </thead>
                <tbody>
                    ${preventiveExaminations.map(exam => `
                        <tr>
                            <td class="age">${exam.age}</td>
                            <td class="specialists">${exam.specialists.join(' • ')}</td>
                            <td class="examinations">${exam.examinations.length > 0 ? exam.examinations.join(' • ') : 'Не проводится'}</td>
                        </tr>
                    `).join('')}
                </tbody>
            </table>

            <div class="footer">
                <p>Сгенерировано: ${new Date().toLocaleDateString('ru-RU')}</p>
                <p>Педиатрическое приложение для врачей</p>
            </div>
        </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleDiagnosisSearch = () => {
    if (!diagnosisSearch.trim()) {
      setSearchResults([]);
      return;
    }

    const searchLower = diagnosisSearch.toLowerCase();
    const results = healthGroupsData.filter(item => 
      item.diagnosis.toLowerCase().includes(searchLower) ||
      item.icdCode.toLowerCase().includes(searchLower) ||
      item.description.toLowerCase().includes(searchLower)
    );
    setSearchResults(results);
  };

  // Приказ 211-н Минздрава РФ от 14.04.2025 "О порядке прохождения несовершеннолетними профилактических медицинских осмотров"
  // Вступает в силу с 01.09.2025
  const preventiveExaminations = [
    {
      age: 'Новорожденные',
      specialists: ['Педиатр'],
      examinations: ['Неонатальный скрининг', 'Аудиоскрининг']
    },
    {
      age: '1 месяц',
      specialists: ['Врач-педиатр', 'Врач детский хирург', 'Врач-офтальмолог'],
      examinations: ['УЗИ органов брюшной полости', 'УЗИ почек', 'УЗИ тазобедренных суставов', 'Нейросонография', 'Эхокардиография', 'Офтальмоскопия в условиях мидриаза']
    },
    {
      age: '2 месяца',
      specialists: ['Врач педиатр'],
      examinations: []
    },
    {
      age: '3 месяца',
      specialists: ['Врач-педиатр', 'Невролог', 'Травматолог-ортопед'],
      examinations: ['Общий анализ крови и мочи']
    },
    {
      age: '4-11 месяцев',
      specialists: ['Врач-педиатр'],
      examinations: []
    },
    {
      age: '12 месяцев',
      specialists: ['Врач-педиатр', 'Невролог', 'Детский хирург', 'Офтальмолог', 'ЛОР', 'Стоматолог'],
      examinations: ['Общий анализ крови', 'Общий анализ мочи', 'Электрокардиография', 'Офтальмоскопия в условиях мидриаза', 'Исследование вызванной отоакустической эмиссии']
    },
    {
      age: '1 год 3 месяца',
      specialists: ['Врач педиатр'],
      examinations: []
    },
    {
      age: '1 год 6 месяцев',
      specialists: ['Врач педиатр', 'Невролог (для детей из групп риска)'],
      examinations: ['Скрининг на выявление группы риска возникновения или нарушения психического развития']
    },
    {
      age: '2 года',
      specialists: ['Врач-педиатр', 'Стоматолог', 'Врач психиатр детский (для детей из групп риска)'],
      examinations: ['Скрининг на выявление группы риска возникновения или нарушения психического развития']
    },
    {
      age: '3 года',
      specialists: ['Врач-педиатр', 'Невролог', 'Детский хирург', 'Офтальмолог', 'Стоматолог', 'ЛОР'],
      examinations: ['Общий анализ крови и общий анализ мочи']
    },
    {
      age: '4 года',
      specialists: ['Врач-педиатр', 'Врач стоматолог'],
      examinations: []
    },
    {
      age: '5 лет',
      specialists: ['Врач-педиатр', 'Врач стоматолог'],
      examinations: []
    },
    {
      age: '6 лет',
      specialists: ['Врач-педиатр', 'Невролог', 'Детский хирург', 'Стоматолог', 'Травматолог-ортопед', 'Офтальмолог', 'ЛОР', 'Психиатр детский', 'Гинеколог (для девочек)', 'Уролог (для мальчиков)', 'Детский эндокринолог'],
      examinations: ['Общий анализ крови', 'Общий анализ мочи', 'УЗИ органов брюшной полости', 'УЗИ почек', 'Эхокардиография', 'Электрокардиография', 'Исследование вызванной отоакустической эмиссии', 'Исследование уровня холестерина для детей из группы риска']
    },
    {
      age: '7 лет',
      specialists: ['Врач-педиатр', 'Невролог', 'Стоматолог детский', 'Врач-офтальмолог'],
      examinations: []
    },
    {
      age: '8 лет',
      specialists: ['Врач-педиатр', 'Детский стоматолог'],
      examinations: []
    },
    {
      age: '9 лет',
      specialists: ['Врач-педиатр', 'Детский стоматолог'],
      examinations: []
    },
    {
      age: '10 лет',
      specialists: ['Врач-педиатр', 'Невролог', 'Стоматолог детский', 'Детский эндокринолог', 'Детский хирург', 'Офтальмолог'],
      examinations: ['Общий анализ крови', 'Общий анализ мочи', 'Исследования уровня холестерина экспресс-методом']
    },
    {
      age: '11 лет',
      specialists: ['Врач-педиатр', 'Детский стоматолог'],
      examinations: []
    },
    {
      age: '12 лет',
      specialists: ['Врач-педиатр', 'Детский стоматолог', 'Психиатр детский', 'Травматолог-ортопед'],
      examinations: []
    },
    {
      age: '13 лет',
      specialists: ['Врач-педиатр', 'Детский стоматолог', 'Уролог-андролог для мальчиков', 'Акушер-гинеколог для девочек'],
      examinations: []
    },
    {
      age: '14 лет',
      specialists: ['Врач-педиатр', 'Детский стоматолог', 'Уролог-андролог для мальчиков', 'Акушер-гинеколог для девочек', 'Детский хирург', 'Детский психиатр'],
      examinations: []
    },
    {
      age: '15 лет',
      specialists: ['Врач-педиатр', 'Детский стоматолог', 'Уролог-андролог для мальчиков', 'Акушер-гинеколог для девочек', 'Детский хирург', 'Детский психиатр', 'Детский эндокринолог', 'Невролог', 'Травматолог-ортопед', 'Офтальмолог', 'ЛОР'],
      examinations: ['Общий анализ крови', 'Общий анализ мочи', 'УЗИ органов брюшной полости', 'УЗИ почек', 'Электрокардиография']
    },
    {
      age: '16 лет',
      specialists: ['Врач-педиатр', 'Детский стоматолог', 'Уролог-андролог для мальчиков', 'Акушер-гинеколог для девочек', 'Детский хирург', 'Детский психиатр', 'Невролог', 'Офтальмолог'],
      examinations: ['Общий анализ крови', 'Общий анализ мочи']
    },
    {
      age: '17 лет',
      specialists: ['Врач-педиатр', 'Детский стоматолог', 'Уролог-андролог для мальчиков', 'Акушер-гинеколог для девочек', 'Детский хирург', 'Детский психиатр', 'Детский эндокринолог', 'Невролог', 'Травматолог-ортопед', 'Офтальмолог', 'ЛОР'],
      examinations: ['Общий анализ крови', 'Общий анализ мочи', 'Электрокардиография']
    }
  ];

  const healthGroupsData = [
    {
      diagnosis: 'Аллергический ринит',
      icdCode: 'J30',
      healthGroup: 'II',
      description: 'Аллергические реакции на пищевые продукты, лекарства, вакцины и др.',
      category: 'Аллергические заболевания'
    },
    {
      diagnosis: 'Бронхиальная астма',
      icdCode: 'J45',
      healthGroup: 'III, IV',
      description: 'В зависимости от степени компенсации и частоты обострений',
      category: 'Заболевания органов дыхания'
    },
    {
      diagnosis: 'Атопический дерматит',
      icdCode: 'L20',
      healthGroup: 'III, IV',
      description: 'В зависимости от локализации процесса, распространенности, интенсивности кожного процесса',
      category: 'Заболевания кожи'
    },
    {
      diagnosis: 'Экзема',
      icdCode: 'L23',
      healthGroup: 'III, IV',
      description: 'В зависимости от локализации процесса, распространенности, интенсивности кожного процесса',
      category: 'Заболевания кожи'
    },
    {
      diagnosis: 'Крапивница',
      icdCode: 'L50',
      healthGroup: 'II, III, IV',
      description: 'Локализованная - II; генерализованная, рецидивирующая III; генерализованная, рецидивирующая с отеком Квинке или анафилактич. шоком (в анамнезе) - IV',
      category: 'Заболевания кожи'
    },
    {
      diagnosis: 'Псориаз',
      icdCode: 'L40',
      healthGroup: 'III, IV',
      description: 'В зависимости от степени компенсации',
      category: 'Заболевания кожи'
    },
    {
      diagnosis: 'Сколиоз',
      icdCode: 'M41',
      healthGroup: 'III, IV',
      description: 'Рентгенологически и клинически не более 2ст. искривления III; искривление более 2ст. или наличие нарушений функции внутренних органов IV',
      category: 'Заболевания костно-мышечной системы'
    },
    {
      diagnosis: 'Кифоз',
      icdCode: 'M40',
      healthGroup: 'II',
      description: 'При отсутствии рентгенологических признаков других изменений позвоночника',
      category: 'Заболевания костно-мышечной системы'
    },
    {
      diagnosis: 'Нарушения осанки',
      icdCode: 'M40',
      healthGroup: 'II',
      description: 'При отсутствии рентгенологических признаков других изменений позвоночника',
      category: 'Заболевания костно-мышечной системы'
    },
    {
      diagnosis: 'Плоскостопие',
      icdCode: 'M21.4',
      healthGroup: 'III',
      description: 'По результатам плантографии',
      category: 'Заболевания костно-мышечной системы'
    },
    {
      diagnosis: 'Вальгусная деформация стопы',
      icdCode: 'M21.0',
      healthGroup: 'III',
      description: 'Деформация стопы с отклонением кнаружи',
      category: 'Заболевания костно-мышечной системы'
    },
    {
      diagnosis: 'Юношеский остеохондроз',
      icdCode: 'M42',
      healthGroup: 'III, IV',
      description: 'Без выраженных клинических проявлений - III; с выраженным локальным и/или корешковым болевым синдромом, парестезиями и др. клиническими проявлениями - IV',
      category: 'Заболевания костно-мышечной системы'
    },
    {
      diagnosis: 'Юношеский артрит',
      icdCode: 'M08',
      healthGroup: 'III, IV',
      description: 'В зависимости от компенсации процесса',
      category: 'Заболевания костно-мышечной системы'
    },
    {
      diagnosis: 'Инсулинзависимый сахарный диабет (I типа)',
      icdCode: 'E10.0',
      healthGroup: 'III, IV',
      description: 'В зависимости от степени компенсации',
      category: 'Эндокринные заболевания'
    },
    {
      diagnosis: 'Сахарный диабет (II типа)',
      icdCode: 'E11',
      healthGroup: 'II, III',
      description: 'В зависимости от степени компенсации',
      category: 'Эндокринные заболевания'
    },
    {
      diagnosis: 'Нарушения толерантности к глюкозе',
      icdCode: 'R73.0',
      healthGroup: 'II',
      description: 'Предиабетическое состояние',
      category: 'Эндокринные заболевания'
    },
    {
      diagnosis: 'Гиповитаминоз',
      icdCode: 'E50-E56',
      healthGroup: 'II',
      description: 'Дефицит витаминов',
      category: 'Эндокринные заболевания'
    },
    {
      diagnosis: 'Задержка полового созревания',
      icdCode: 'E30.0',
      healthGroup: 'II, III, IV',
      description: 'Без сопутствующих эндокринных заболеваний - II, при их наличии и в зависимости от тяжести III или IV',
      category: 'Эндокринные заболевания'
    },
    {
      diagnosis: 'Преждевременное половое созревание',
      icdCode: 'E30.1',
      healthGroup: 'II, III, IV',
      description: 'Без сопутствующих эндокринных заболеваний - II, при их наличии и в зависимости от тяжести - III или IV',
      category: 'Эндокринные заболевания'
    },
    {
      diagnosis: 'Хронический гастрит',
      icdCode: 'K29',
      healthGroup: 'III, IV',
      description: 'В зависимости от степени компенсации и частоты обострений',
      category: 'Заболевания органов пищеварения'
    },
    {
      diagnosis: 'Хронический дуоденит',
      icdCode: 'K29',
      healthGroup: 'III, IV',
      description: 'В зависимости от степени компенсации и частоты обострений',
      category: 'Заболевания органов пищеварения'
    },
    {
      diagnosis: 'Дискинезия желчевыводящих путей',
      icdCode: 'K82.8',
      healthGroup: 'II, III',
      description: 'В зависимости от выраженности клинических проявлений',
      category: 'Заболевания органов пищеварения'
    },
    {
      diagnosis: 'Хронический пиелонефрит',
      icdCode: 'N11',
      healthGroup: 'III, IV',
      description: 'В зависимости от степени компенсации и частоты обострений',
      category: 'Заболевания мочевыделительной системы'
    },
    {
      diagnosis: 'Хронический цистит',
      icdCode: 'N30',
      healthGroup: 'III, IV',
      description: 'В зависимости от степени компенсации и частоты обострений',
      category: 'Заболевания мочевыделительной системы'
    },
    {
      diagnosis: 'Миопия',
      icdCode: 'H52.1',
      healthGroup: 'II, III, IV',
      description: 'В зависимости от степени миопии и наличия осложнений',
      category: 'Заболевания глаз'
    },
    {
      diagnosis: 'Гиперметропия',
      icdCode: 'H52.0',
      healthGroup: 'II, III',
      description: 'В зависимости от степени гиперметропии',
      category: 'Заболевания глаз'
    },
    {
      diagnosis: 'Астигматизм',
      icdCode: 'H52.2',
      healthGroup: 'II, III',
      description: 'В зависимости от степени астигматизма',
      category: 'Заболевания глаз'
    },
    {
      diagnosis: 'Хронический тонзиллит',
      icdCode: 'J35.0',
      healthGroup: 'II, III',
      description: 'В зависимости от степени компенсации',
      category: 'Заболевания ЛОР-органов'
    },
    {
      diagnosis: 'Хронический ринит',
      icdCode: 'J31.0',
      healthGroup: 'II, III',
      description: 'В зависимости от степени компенсации',
      category: 'Заболевания ЛОР-органов'
    },
    {
      diagnosis: 'Хронический синусит',
      icdCode: 'J32',
      healthGroup: 'II, III',
      description: 'В зависимости от степени компенсации',
      category: 'Заболевания ЛОР-органов'
    },
    {
      diagnosis: 'Хронический отит',
      icdCode: 'H65',
      healthGroup: 'II, III',
      description: 'В зависимости от степени компенсации',
      category: 'Заболевания ЛОР-органов'
    },
    {
      diagnosis: 'Вегетососудистая дистония',
      icdCode: 'G90.9',
      healthGroup: 'II, III',
      description: 'В зависимости от выраженности клинических проявлений',
      category: 'Заболевания нервной системы'
    },
    {
      diagnosis: 'Церебрастенический синдром',
      icdCode: 'F06.6',
      healthGroup: 'II, III',
      description: 'В зависимости от выраженности клинических проявлений',
      category: 'Заболевания нервной системы'
    },
    {
      diagnosis: 'Синдром дефицита внимания с гиперактивностью',
      icdCode: 'F90',
      healthGroup: 'II, III',
      description: 'В зависимости от выраженности клинических проявлений',
      category: 'Заболевания нервной системы'
    }
  ];

  const filteredExaminations = preventiveExaminations.filter(exam =>
    exam.age.toLowerCase().includes(searchTerm.toLowerCase()) ||
    exam.examinations.some(examination => 
      examination.toLowerCase().includes(searchTerm.toLowerCase())
    ) ||
    exam.specialists.some(specialist => 
      specialist.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  return (
    <Box>
      <Box sx={{ mb: 4, px: { xs: 2, sm: 0 } }}>
        <Typography 
          variant="h1" 
          sx={{ 
            mb: 2,
            fontSize: { xs: '1.75rem', sm: '2.125rem' },
            fontWeight: 600,
            color: '#2D3748'
          }}
        >
          Приказы и нормативы
        </Typography>
      </Box>

      <Box sx={{ 
        borderBottom: 1, 
        borderColor: 'divider', 
        mb: 3,
        overflowX: 'auto',
        '& .MuiTabs-scroller': {
          overflowX: 'auto !important'
        }
      }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange}
          variant={window.innerWidth < 768 ? 'scrollable' : 'standard'}
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              minWidth: { xs: 'auto', sm: 160 },
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              fontWeight: 500,
              px: { xs: 1, sm: 2 }
            }
          }}
        >
          <Tab label="211-н и осмотры" />
          <Tab label="Группы здоровья" />
          <Tab label="МСЭ" />
          <Tab label="Экстренное извещение" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Box sx={{ 
          mb: 3, 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' }, 
          gap: 2, 
          alignItems: { xs: 'stretch', sm: 'center' },
          px: { xs: 1, sm: 0 }
        }}>
          <TextField
            fullWidth
            placeholder="Поиск по возрасту, обследованиям или специалистам..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#718096' }} />
                </InputAdornment>
              ),
            }}
            sx={{ 
              maxWidth: 600,
              '& .MuiInputBase-root': {
                fontSize: { xs: '14px', sm: '16px' }
              }
            }}
          />
          <Box sx={{ 
            display: 'flex', 
            gap: 1, 
            flexShrink: 0,
            flexDirection: { xs: 'column', sm: 'row' },
            width: { xs: '100%', sm: 'auto' }
          }}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={generatePDFMemo}
              sx={{ 
                minWidth: { xs: '100%', sm: 'auto' },
                whiteSpace: 'nowrap',
                borderColor: '#4A90E2',
                color: '#4A90E2',
                fontSize: { xs: '13px', sm: '14px' },
                py: { xs: 1, sm: 1.5 },
                '&:hover': {
                  borderColor: '#357ABD',
                  backgroundColor: 'rgba(74, 144, 226, 0.04)'
                }
              }}
            >
              Скачать
            </Button>
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={printMemo}
              sx={{ 
                minWidth: { xs: '100%', sm: 'auto' },
                whiteSpace: 'nowrap',
                borderColor: '#50C878',
                color: '#50C878',
                fontSize: { xs: '13px', sm: '14px' },
                py: { xs: 1, sm: 1.5 },
                '&:hover': {
                  borderColor: '#45B06B',
                  backgroundColor: 'rgba(80, 200, 120, 0.04)'
                }
              }}
            >
              Печать
            </Button>
          </Box>
        </Box>

        {/* Мобильная версия таблицы */}
        <Box sx={{ display: { xs: 'block', md: 'none' }, px: 1 }}>
          {filteredExaminations.map((exam, index) => (
            <Card key={index} sx={{ 
              mb: 2, 
              borderRadius: 3,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              overflow: 'hidden'
            }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <ScheduleIcon sx={{ color: '#4A90E2', fontSize: 18, mr: 1 }} />
                  <Typography variant="h6" sx={{ 
                    fontWeight: 600, 
                    color: '#2D3748',
                    fontSize: '16px'
                  }}>
                    {exam.age}
                  </Typography>
                </Box>
                
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="body2" sx={{ 
                    fontWeight: 600, 
                    color: '#4A90E2', 
                    mb: 0.5,
                    fontSize: '13px'
                  }}>
                    Специалисты:
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    lineHeight: 1.4,
                    fontSize: '14px',
                    color: '#555'
                  }}>
                    {exam.specialists.join(', ')}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="body2" sx={{ 
                    fontWeight: 600, 
                    color: '#4A90E2', 
                    mb: 0.5,
                    fontSize: '13px'
                  }}>
                    Обследования:
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    lineHeight: 1.4,
                    fontSize: '14px',
                    color: '#555'
                  }}>
                    {exam.examinations.length > 0 ? exam.examinations.join(', ') : 'Не проводится'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          ))}
          
          {filteredExaminations.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" sx={{ color: '#718096' }}>
                По вашему запросу ничего не найдено
              </Typography>
            </Box>
          )}
        </Box>

        {/* Десктопная версия таблицы */}
        <TableContainer 
          component={Paper} 
          sx={{ 
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            display: { xs: 'none', md: 'block' }
          }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#F8FAFC' }}>
                <TableCell sx={{ fontWeight: 600, color: '#2D3748' }}>
                  Возраст
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#2D3748' }}>
                  Специалисты
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#2D3748' }}>
                  Обследования
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredExaminations.map((exam, index) => (
                <TableRow key={index} hover>
                  <TableCell sx={{ fontWeight: 500, minWidth: 120 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ScheduleIcon sx={{ color: '#4A90E2', fontSize: 20 }} />
                      {exam.age}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
                      {exam.specialists.join(' • ')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
                      {exam.examinations.length > 0 ? exam.examinations.join(' • ') : 'Не проводится'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Box sx={{ mb: 3, px: { xs: 1, sm: 0 } }}>
          <Typography 
            variant="h4" 
            sx={{ 
              mb: 2,
              fontSize: { xs: '1.5rem', sm: '2rem' },
              fontWeight: 600,
              color: '#2D3748'
            }}
          >
            Определение группы здоровья по диагнозу
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              color: '#718096', 
              mb: 3,
              fontSize: { xs: '14px', sm: '16px' },
              lineHeight: 1.6
            }}
          >
            Поиск группы здоровья по диагнозу или коду МКБ-10 на основе приказа Минздрава России 
            от 30.12.2003 № 621 "О комплексной оценке состояния здоровья детей".
          </Typography>
          
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            mb: 3,
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' }
          }}>
            <TextField
              fullWidth
              placeholder="Введите диагноз или код МКБ-10 (например: астма, J45, сколиоз, M41)..."
              value={diagnosisSearch}
              onChange={(e) => setDiagnosisSearch(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleDiagnosisSearch();
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#718096' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ 
                maxWidth: 600,
                '& .MuiInputBase-root': {
                  fontSize: { xs: '14px', sm: '16px' }
                }
              }}
            />
            <Button
              variant="contained"
              onClick={handleDiagnosisSearch}
              sx={{ 
                minWidth: { xs: '100%', sm: 120 },
                fontSize: { xs: '13px', sm: '14px' },
                py: { xs: 1, sm: 1.5 }
              }}
            >
              Найти
            </Button>
          </Box>
        </Box>

        {searchResults.length > 0 && (
          <Box sx={{ mb: 3, px: { xs: 1, sm: 0 } }}>
            <Typography 
              variant="h5" 
              sx={{ 
                mb: 2,
                fontSize: { xs: '1.25rem', sm: '1.5rem' },
                fontWeight: 600,
                color: '#2D3748'
              }}
            >
              Результаты поиска ({searchResults.length})
            </Typography>
            <Box sx={{ 
              display: 'grid',
              gridTemplateColumns: { 
                xs: '1fr', 
                sm: 'repeat(auto-fit, minmax(300px, 1fr))'
              },
              gap: 2
            }}>
              {searchResults.map((result, index) => (
                <Card key={index} sx={{ 
                  height: '100%',
                  borderRadius: 3,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  overflow: 'hidden'
                }}>
                  <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Box sx={{ 
                      display: 'flex', 
                      gap: 1, 
                      mb: 2,
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' }
                    }}>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          flex: 1,
                          fontSize: { xs: '16px', sm: '18px' },
                          fontWeight: 600,
                          color: '#2D3748'
                        }}
                      >
                        {result.diagnosis}
                      </Typography>
                      <Chip
                        label={`Группа ${result.healthGroup}`}
                        sx={{
                          backgroundColor: result.healthGroup.includes('I') ? '#E8F5E8' :
                                         result.healthGroup.includes('II') ? '#FFF3CD' :
                                         result.healthGroup.includes('III') ? '#FFE8E8' :
                                         result.healthGroup.includes('IV') ? '#FFE8E8' : '#FFE8E8',
                          color: result.healthGroup.includes('I') ? '#50C878' :
                                 result.healthGroup.includes('II') ? '#856404' :
                                 result.healthGroup.includes('III') ? '#FF6B6B' :
                                 result.healthGroup.includes('IV') ? '#FF6B6B' : '#FF6B6B',
                          fontWeight: 600,
                          fontSize: { xs: '11px', sm: '12px' }
                        }}
                      />
                    </Box>
                    
                    <Box sx={{ mb: 2 }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 600, 
                          mb: 0.5,
                          fontSize: { xs: '13px', sm: '14px' }
                        }}
                      >
                        Код МКБ-10:
                      </Typography>
                      <Chip
                        label={result.icdCode}
                        size="small"
                        sx={{ 
                          backgroundColor: '#E3F2FD', 
                          color: '#4A90E2',
                          fontSize: { xs: '11px', sm: '12px' }
                        }}
                      />
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 600, 
                          mb: 0.5,
                          fontSize: { xs: '13px', sm: '14px' }
                        }}
                      >
                        Категория:
                      </Typography>
                      <Chip
                        label={result.category}
                        size="small"
                        sx={{ 
                          backgroundColor: '#F3E5F5', 
                          color: '#9C27B0',
                          fontSize: { xs: '11px', sm: '12px' }
                        }}
                      />
                    </Box>

                    <Box>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 600, 
                          mb: 0.5,
                          fontSize: { xs: '13px', sm: '14px' }
                        }}
                      >
                        Критерии отнесения:
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: '#718096',
                          fontSize: { xs: '13px', sm: '14px' },
                          lineHeight: 1.5
                        }}
                      >
                        {result.description}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Box>
        )}

        {diagnosisSearch && searchResults.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" sx={{ color: '#718096' }}>
              По вашему запросу ничего не найдено
            </Typography>
            <Typography variant="body2" sx={{ color: '#718096', mt: 1 }}>
              Попробуйте изменить поисковый запрос или использовать код МКБ-10
            </Typography>
          </Box>
        )}

        {!diagnosisSearch && (
          <Box sx={{ px: { xs: 1, sm: 0 } }}>
            <Typography 
              variant="h5" 
              sx={{ 
                mb: 2,
                fontSize: { xs: '1.25rem', sm: '1.5rem' },
                fontWeight: 600,
                color: '#2D3748'
              }}
            >
              Группы здоровья детей
            </Typography>
            <Box sx={{ 
              display: 'grid',
              gridTemplateColumns: { 
                xs: '1fr', 
                sm: 'repeat(auto-fit, minmax(280px, 1fr))'
              },
              gap: { xs: 2, sm: 3 }
            }}>
              <Card sx={{ 
                borderRadius: 3,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                overflow: 'hidden'
              }}>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  <Box sx={{ 
                    display: 'flex', 
                    gap: 1, 
                    mb: 2,
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'center' }
                  }}>
                    <Box sx={{ 
                      width: { xs: 32, sm: 40 }, 
                      height: { xs: 32, sm: 40 }, 
                      borderRadius: '50%', 
                      backgroundColor: '#E8F5E8', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          color: '#50C878', 
                          fontWeight: 600,
                          fontSize: { xs: '16px', sm: '20px' }
                        }}
                      >
                        I
                      </Typography>
                    </Box>
                    <Typography 
                      variant="h5"
                      sx={{ 
                        fontSize: { xs: '18px', sm: '20px' },
                        fontWeight: 600,
                        color: '#2D3748'
                      }}
                    >
                      I группа здоровья
                    </Typography>
                  </Box>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: '#718096',
                      fontSize: { xs: '13px', sm: '14px' },
                      lineHeight: 1.5
                    }}
                  >
                    Здоровые дети, имеющие нормальное физическое и психическое развитие, 
                    не имеющие анатомических дефектов, функциональных и морфофункциональных отклонений.
                  </Typography>
                </CardContent>
              </Card>

              <Card sx={{ 
                borderRadius: 3,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                overflow: 'hidden'
              }}>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  <Box sx={{ 
                    display: 'flex', 
                    gap: 1, 
                    mb: 2,
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'center' }
                  }}>
                    <Box sx={{ 
                      width: { xs: 32, sm: 40 }, 
                      height: { xs: 32, sm: 40 }, 
                      borderRadius: '50%', 
                      backgroundColor: '#FFF3CD', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          color: '#856404', 
                          fontWeight: 600,
                          fontSize: { xs: '16px', sm: '20px' }
                        }}
                      >
                        II
                      </Typography>
                    </Box>
                    <Typography 
                      variant="h5"
                      sx={{ 
                        fontSize: { xs: '18px', sm: '20px' },
                        fontWeight: 600,
                        color: '#2D3748'
                      }}
                    >
                      II группа здоровья
                    </Typography>
                  </Box>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: '#718096',
                      fontSize: { xs: '13px', sm: '14px' },
                      lineHeight: 1.5
                    }}
                  >
                    Дети, у которых отсутствуют хронические заболевания, но имеются некоторые 
                    функциональные и морфофункциональные нарушения, реконвалесценты.
                  </Typography>
                </CardContent>
              </Card>

              <Card sx={{ 
                borderRadius: 3,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                overflow: 'hidden'
              }}>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  <Box sx={{ 
                    display: 'flex', 
                    gap: 1, 
                    mb: 2,
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'center' }
                  }}>
                    <Box sx={{ 
                      width: { xs: 32, sm: 40 }, 
                      height: { xs: 32, sm: 40 }, 
                      borderRadius: '50%', 
                      backgroundColor: '#FFE8E8', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          color: '#FF6B6B', 
                          fontWeight: 600,
                          fontSize: { xs: '16px', sm: '20px' }
                        }}
                      >
                        III
                      </Typography>
                    </Box>
                    <Typography 
                      variant="h5"
                      sx={{ 
                        fontSize: { xs: '18px', sm: '20px' },
                        fontWeight: 600,
                        color: '#2D3748'
                      }}
                    >
                      III группа здоровья
                    </Typography>
                  </Box>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: '#718096',
                      fontSize: { xs: '13px', sm: '14px' },
                      lineHeight: 1.5
                    }}
                  >
                    Дети, страдающие хроническими заболеваниями в стадии клинической ремиссии, 
                    с редкими обострениями, с сохраненными или компенсированными функциональными возможностями.
                  </Typography>
                </CardContent>
              </Card>

              <Card sx={{ 
                borderRadius: 3,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                overflow: 'hidden'
              }}>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  <Box sx={{ 
                    display: 'flex', 
                    gap: 1, 
                    mb: 2,
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'center' }
                  }}>
                    <Box sx={{ 
                      width: { xs: 32, sm: 40 }, 
                      height: { xs: 32, sm: 40 }, 
                      borderRadius: '50%', 
                      backgroundColor: '#FFE8E8', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          color: '#FF6B6B', 
                          fontWeight: 600,
                          fontSize: { xs: '16px', sm: '20px' }
                        }}
                      >
                        IV
                      </Typography>
                    </Box>
                    <Typography 
                      variant="h5"
                      sx={{ 
                        fontSize: { xs: '18px', sm: '20px' },
                        fontWeight: 600,
                        color: '#2D3748'
                      }}
                    >
                      IV группа здоровья
                    </Typography>
                  </Box>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: '#718096',
                      fontSize: { xs: '13px', sm: '14px' },
                      lineHeight: 1.5
                    }}
                  >
                    Дети, страдающие хроническими заболеваниями в активной стадии и стадии 
                    нестойкой клинической ремиссии с частыми обострениями.
                  </Typography>
                </CardContent>
              </Card>

              <Card sx={{ 
                borderRadius: 3,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                overflow: 'hidden'
              }}>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  <Box sx={{ 
                    display: 'flex', 
                    gap: 1, 
                    mb: 2,
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'center' }
                  }}>
                    <Box sx={{ 
                      width: { xs: 32, sm: 40 }, 
                      height: { xs: 32, sm: 40 }, 
                      borderRadius: '50%', 
                      backgroundColor: '#FFE8E8', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          color: '#FF6B6B', 
                          fontWeight: 600,
                          fontSize: { xs: '16px', sm: '20px' }
                        }}
                      >
                        V
                      </Typography>
                    </Box>
                    <Typography 
                      variant="h5"
                      sx={{ 
                        fontSize: { xs: '18px', sm: '20px' },
                        fontWeight: 600,
                        color: '#2D3748'
                      }}
                    >
                      V группа здоровья
                    </Typography>
                  </Box>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: '#718096',
                      fontSize: { xs: '13px', sm: '14px' },
                      lineHeight: 1.5
                    }}
                  >
                    Дети, страдающие тяжелыми хроническими заболеваниями, с редкими клиническими 
                    ремиссиями, с частыми обострениями, непрерывно рецидивирующим течением, 
                    с выраженной декомпенсацией функциональных возможностей организма.
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            <Box sx={{ 
              mt: 4, 
              p: { xs: 2, sm: 3 }, 
              backgroundColor: '#F8F9FA', 
              borderRadius: 2, 
              border: '1px solid #E9ECEF'
            }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#718096', 
                  mb: 2,
                  fontSize: { xs: '13px', sm: '14px' },
                  lineHeight: 1.5
                }}
              >
                Классификация групп здоровья основана на приказе Минздрава России от 30.12.2003 № 621 
                "О комплексной оценке состояния здоровья детей".
              </Typography>
              <Button
                variant="outlined"
                startIcon={<OpenInNewIcon />}
                href="https://normativ.kontur.ru/document?moduleId=1&documentId=55198"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ 
                  borderColor: '#4A90E2',
                  color: '#4A90E2',
                  fontSize: { xs: '13px', sm: '14px' },
                  py: { xs: 1, sm: 1.5 },
                  '&:hover': {
                    borderColor: '#357ABD',
                    backgroundColor: 'rgba(74, 144, 226, 0.04)'
                  }
                }}
              >
                Открыть приказ № 621
              </Button>
            </Box>
          </Box>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Box sx={{ mb: 3, px: { xs: 1, sm: 0 } }}>
          <Typography 
            variant="h2" 
            sx={{ 
              mb: 2, 
              color: '#2D3748',
              fontSize: { xs: '1.5rem', sm: '2rem' },
              fontWeight: 600
            }}
          >
            Медико-социальная экспертиза
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              color: '#718096', 
              mb: 3,
              fontSize: { xs: '14px', sm: '16px' },
              lineHeight: 1.6
            }}
          >
            Форма 088-у и нормативные документы по МСЭ
          </Typography>
        </Box>

        <Card sx={{ 
          mb: 3,
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Box sx={{ 
              display: 'flex', 
              mb: 2,
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' }
            }}>
              <DescriptionIcon sx={{ 
                color: '#50C878', 
                mr: { xs: 0, sm: 1 },
                mb: { xs: 1, sm: 0 },
                fontSize: { xs: '24px', sm: '28px' }
              }} />
              <Typography 
                variant="h3" 
                sx={{ 
                  color: '#2D3748',
                  fontSize: { xs: '1.25rem', sm: '1.5rem' },
                  fontWeight: 600,
                  lineHeight: 1.3
                }}
              >
                Форма 088-у "Направление на медико-социальную экспертизу"
              </Typography>
            </Box>
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#718096', 
                mb: 3,
                fontSize: { xs: '13px', sm: '14px' },
                lineHeight: 1.5
              }}
            >
              Основные пункты оформления направления на МСЭ для детей
            </Typography>
            
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, 
              gap: { xs: 2, sm: 3 }
            }}>
              <Box>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mb: 2, 
                    color: '#2D3748',
                    fontSize: { xs: '16px', sm: '18px' },
                    fontWeight: 600
                  }}
                >
                  📋 Обязательные разделы формы
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ 
                      width: { xs: 6, sm: 8 }, 
                      height: { xs: 6, sm: 8 }, 
                      borderRadius: '50%', 
                      backgroundColor: '#4A90E2',
                      flexShrink: 0
                    }} />
                    <Typography 
                      variant="body2"
                      sx={{ 
                        fontSize: { xs: '13px', sm: '14px' },
                        lineHeight: 1.4
                      }}
                    >
                      Паспортные данные ребенка
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ 
                      width: { xs: 6, sm: 8 }, 
                      height: { xs: 6, sm: 8 }, 
                      borderRadius: '50%', 
                      backgroundColor: '#4A90E2',
                      flexShrink: 0
                    }} />
                    <Typography 
                      variant="body2"
                      sx={{ 
                        fontSize: { xs: '13px', sm: '14px' },
                        lineHeight: 1.4
                      }}
                    >
                      Анамнез заболевания
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ 
                      width: { xs: 6, sm: 8 }, 
                      height: { xs: 6, sm: 8 }, 
                      borderRadius: '50%', 
                      backgroundColor: '#4A90E2',
                      flexShrink: 0
                    }} />
                    <Typography 
                      variant="body2"
                      sx={{ 
                        fontSize: { xs: '13px', sm: '14px' },
                        lineHeight: 1.4
                      }}
                    >
                      Результаты обследований
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ 
                      width: { xs: 6, sm: 8 }, 
                      height: { xs: 6, sm: 8 }, 
                      borderRadius: '50%', 
                      backgroundColor: '#4A90E2',
                      flexShrink: 0
                    }} />
                    <Typography 
                      variant="body2"
                      sx={{ 
                        fontSize: { xs: '13px', sm: '14px' },
                        lineHeight: 1.4
                      }}
                    >
                      Заключения специалистов
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ 
                      width: { xs: 6, sm: 8 }, 
                      height: { xs: 6, sm: 8 }, 
                      borderRadius: '50%', 
                      backgroundColor: '#4A90E2',
                      flexShrink: 0
                    }} />
                    <Typography 
                      variant="body2"
                      sx={{ 
                        fontSize: { xs: '13px', sm: '14px' },
                        lineHeight: 1.4
                      }}
                    >
                      Рекомендации по лечению
                    </Typography>
                  </Box>
                </Box>
              </Box>
              
              <Box>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mb: 2, 
                    color: '#2D3748',
                    fontSize: { xs: '16px', sm: '18px' },
                    fontWeight: 600
                  }}
                >
                  ⚠️ Критерии направления
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ 
                      width: { xs: 6, sm: 8 }, 
                      height: { xs: 6, sm: 8 }, 
                      borderRadius: '50%', 
                      backgroundColor: '#FF6B6B',
                      flexShrink: 0
                    }} />
                    <Typography 
                      variant="body2"
                      sx={{ 
                        fontSize: { xs: '13px', sm: '14px' },
                        lineHeight: 1.4
                      }}
                    >
                      Стойкие нарушения функций организма
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ 
                      width: { xs: 6, sm: 8 }, 
                      height: { xs: 6, sm: 8 }, 
                      borderRadius: '50%', 
                      backgroundColor: '#FF6B6B',
                      flexShrink: 0
                    }} />
                    <Typography 
                      variant="body2"
                      sx={{ 
                        fontSize: { xs: '13px', sm: '14px' },
                        lineHeight: 1.4
                      }}
                    >
                      Ограничение жизнедеятельности
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ 
                      width: { xs: 6, sm: 8 }, 
                      height: { xs: 6, sm: 8 }, 
                      borderRadius: '50%', 
                      backgroundColor: '#FF6B6B',
                      flexShrink: 0
                    }} />
                    <Typography 
                      variant="body2"
                      sx={{ 
                        fontSize: { xs: '13px', sm: '14px' },
                        lineHeight: 1.4
                      }}
                    >
                      Необходимость социальной защиты
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ 
                      width: { xs: 6, sm: 8 }, 
                      height: { xs: 6, sm: 8 }, 
                      borderRadius: '50%', 
                      backgroundColor: '#FF6B6B',
                      flexShrink: 0
                    }} />
                    <Typography 
                      variant="body2"
                      sx={{ 
                        fontSize: { xs: '13px', sm: '14px' },
                        lineHeight: 1.4
                      }}
                    >
                      Прогрессирование заболевания
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <DescriptionIcon sx={{ color: '#9C27B0', mr: 1 }} />
              <Typography variant="h3" sx={{ color: '#2D3748' }}>
                Постановление Правительства РФ об инвалидности
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: '#718096', mb: 3 }}>
              Основные тезисы по установлению инвалидности в Российской Федерации
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#2D3748' }}>
                🎯 Ключевые принципы
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
                <Box sx={{ p: 2, backgroundColor: '#F8F9FA', borderRadius: 1, border: '1px solid #E9ECEF' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    Критерии инвалидности
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#718096', fontSize: '0.875rem' }}>
                    Нарушение здоровья со стойким расстройством функций организма
                  </Typography>
                </Box>
                <Box sx={{ p: 2, backgroundColor: '#F8F9FA', borderRadius: 1, border: '1px solid #E9ECEF' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    Категории инвалидности
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#718096', fontSize: '0.875rem' }}>
                    I, II, III группы инвалидности и категория "ребенок-инвалид"
                  </Typography>
                </Box>
                <Box sx={{ p: 2, backgroundColor: '#F8F9FA', borderRadius: 1, border: '1px solid #E9ECEF' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    Сроки установления
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#718096', fontSize: '0.875rem' }}>
                    Инвалидность устанавливается на 1 год, 2 года, 5 лет или бессрочно
                  </Typography>
                </Box>
                <Box sx={{ p: 2, backgroundColor: '#F8F9FA', borderRadius: 1, border: '1px solid #E9ECEF' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    Переосвидетельствование
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#718096', fontSize: '0.875rem' }}>
                    Проводится в соответствии с установленными сроками
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<OpenInNewIcon />}
                href="https://base.garant.ru/12186004/"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ 
                  backgroundColor: '#9C27B0',
                  '&:hover': { backgroundColor: '#7B1FA2' }
                }}
              >
                Открыть постановление
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                href="https://base.garant.ru/12186004/"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ borderColor: '#9C27B0', color: '#9C27B0' }}
              >
                Скачать документ
              </Button>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <DescriptionIcon sx={{ color: '#4A90E2', mr: 1 }} />
              <Typography variant="h3" sx={{ color: '#2D3748' }}>
                Приказ Минтруда России № 259н и Минздрава России № 238н от 16.11.2024
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: '#718096', mb: 2 }}>
              "Об утверждении классификаций и критериев, используемых при осуществлении 
              медико-социальной экспертизы граждан федеральными государственными учреждениями 
              медико-социальной экспертизы"
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                href="https://kvd4.ru/wp-content/uploads/2024/11/Приказ-Минтруда-России-N-259н-Минздрава-России-N-238н-от-16.pdf"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ 
                  backgroundColor: '#4A90E2',
                  '&:hover': { backgroundColor: '#357ABD' }
                }}
              >
                Скачать PDF
              </Button>
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={() => window.print()}
                sx={{ borderColor: '#4A90E2', color: '#4A90E2' }}
              >
                Печать
              </Button>
            </Box>
          </CardContent>
        </Card>

        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Важно:</strong> Данный приказ устанавливает новые классификации и критерии 
            для проведения медико-социальной экспертизы. Рекомендуется ознакомиться с полным 
            текстом документа для корректного применения в практической деятельности.
          </Typography>
        </Alert>
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h2" sx={{ mb: 2, color: '#2D3748' }}>
            Экстренное извещение
          </Typography>
          <Typography variant="body1" sx={{ color: '#718096', mb: 3 }}>
            Основные пункты оформления экстренного извещения об инфекционных заболеваниях
          </Typography>
        </Box>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <DescriptionIcon sx={{ color: '#50C878', mr: 1 }} />
              <Typography variant="h3" sx={{ color: '#2D3748' }}>
                Форма 058/у "Экстренное извещение об инфекционном заболевании"
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: '#718096', mb: 3 }}>
              Основные пункты, которые должны быть отражены в экстренном извещении
            </Typography>
            
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
              <Box>
                <Typography variant="h6" sx={{ mb: 2, color: '#2D3748' }}>
                  📋 Обязательные разделы извещения
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#4A90E2' }} />
                    <Typography variant="body2">ФИО пациента</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#4A90E2' }} />
                    <Typography variant="body2">Дата рождения и возраст</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#4A90E2' }} />
                    <Typography variant="body2">Адрес места жительства</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#4A90E2' }} />
                    <Typography variant="body2">Место работы/учебы</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#4A90E2' }} />
                    <Typography variant="body2">Дата заболевания</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#4A90E2' }} />
                    <Typography variant="body2">Дата обращения за медицинской помощью</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#4A90E2' }} />
                    <Typography variant="body2">Предварительный диагноз</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#4A90E2' }} />
                    <Typography variant="body2">Окончательный диагноз</Typography>
                  </Box>
                </Box>
              </Box>

              <Box>
                <Typography variant="h6" sx={{ mb: 2, color: '#2D3748' }}>
                  ⚠️ Дополнительная информация
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#FF6B6B' }} />
                    <Typography variant="body2">Эпидемиологический анамнез</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#FF6B6B' }} />
                    <Typography variant="body2">Контактные лица</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#FF6B6B' }} />
                    <Typography variant="body2">Проведенные противоэпидемические мероприятия</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#FF6B6B' }} />
                    <Typography variant="body2">Лабораторные исследования</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#FF6B6B' }} />
                    <Typography variant="body2">Госпитализация (да/нет)</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#FF6B6B' }} />
                    <Typography variant="body2">Исход заболевания</Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <DescriptionIcon sx={{ color: '#4A90E2', mr: 1 }} />
              <Typography variant="h3" sx={{ color: '#2D3748' }}>
                Сроки подачи экстренного извещения
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: '#718096', mb: 3 }}>
              Временные рамки для подачи экстренного извещения в зависимости от заболевания
            </Typography>
            
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
              <Box>
                <Typography variant="h6" sx={{ mb: 2, color: '#2D3748' }}>
                  🚨 Немедленно (в течение 2 часов)
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#FF4444' }} />
                    <Typography variant="body2">Холера, чума, желтая лихорадка</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#FF4444' }} />
                    <Typography variant="body2">Острые вялые параличи</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#FF4444' }} />
                    <Typography variant="body2">Подозрение на особо опасные инфекции</Typography>
                  </Box>
                </Box>
              </Box>

              <Box>
                <Typography variant="h6" sx={{ mb: 2, color: '#2D3748' }}>
                  ⏰ В течение 12 часов
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#FF9800' }} />
                    <Typography variant="body2">Корь, краснуха, эпидемический паротит</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#FF9800' }} />
                    <Typography variant="body2">Ветряная оспа, скарлатина</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#FF9800' }} />
                    <Typography variant="body2">Коклюш, дифтерия</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#FF9800' }} />
                    <Typography variant="body2">ВИЧ-инфекция</Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <DescriptionIcon sx={{ color: '#4A90E2', mr: 1 }} />
              <Typography variant="h3" sx={{ color: '#2D3748' }}>
                Инструкция о порядке регистрации случаев инфекционных заболеваний
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: '#718096', mb: 2 }}>
              Подробная инструкция о порядке регистрации случаев инфекционных и паразитарных 
              заболеваний в г. Москве, включая правила оформления экстренных извещений
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<OpenInNewIcon />}
                href="https://www.mossanexpert.ru/files/Prilogenie%20№%201.%20Instrukciya%20o%20poryadke%20registracii.pdf"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ 
                  backgroundColor: '#4A90E2',
                  '&:hover': { backgroundColor: '#357ABD' }
                }}
              >
                Открыть инструкцию
              </Button>
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={() => window.print()}
                sx={{ borderColor: '#4A90E2', color: '#4A90E2' }}
              >
                Печать
              </Button>
            </Box>
          </CardContent>
        </Card>

        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Важно:</strong> Экстренное извещение должно быть подано в соответствии с 
            установленными сроками. За нарушение сроков подачи извещения предусмотрена 
            административная ответственность согласно КоАП РФ.
          </Typography>
        </Alert>
      </TabPanel>
    </Box>
  );
};

export default Orders;
